import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, take } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { DataLayerEvent, LoaderService, UtilsService } from 'thkee-common';
import { AuthenticationService } from '../../services/auth/authentication.service';
import { CartActionsV2 } from '../cartV2';
import { CartServiceV2 } from '../cartV2/cart-store.service';
import { AuthActions } from './auth.actions';

@Injectable()
export class AuthEffects {
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      switchMap(({ data: { email, password, remember }, trackerId }) => {
        this.loaderService.loaderAction('loading-me', 'loading');
        return this.authService.login({ email: email, password, remember }, trackerId).pipe(
          map((resp) => {
            return AuthActions.loginSuccess();
          }),
          catchError((error) => {
            this.loaderService.loaderAction('loading-me', 'failed');
            return of(AuthActions.loginError({ error: error.message }));
          })
        );
      })
    )
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      tap(() => {
        this.afterSuccess();
      }),
      // switchMap(() => this.authService.getUser().pipe(map((user) => AuthActions.userLoaded({ user }))))
      switchMap(() =>
        this.authService.getAuthInfo().pipe(map((authInfo) => AuthActions.userAuthInfoLoaded({ authInfo })))
      )
    )
  );

  userLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.userLoad),
      switchMap(() => {
        this.loaderService.loaderAction('loading-user-info', 'loading');
        return this.authService.getUser().pipe(
          map((user) => {
            this.loaderService.loaderAction('loading-user-info', 'success');
            return AuthActions.userLoaded({ user });
          }),
          catchError(() => {
            this.loaderService.loaderAction('loading-user-info', 'failed');
            return of(AuthActions.useAsGuest());
          })
        );
      })
    )
  );

  userLoaded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.userLoaded),
        tap((data) => {
          this.store.dispatch(CartActionsV2.loadCart());
          // this.loaderService.loaderAction('loading-me', 'success');
          // this.afterSuccess();

          // this.store.dispatch(CartActionsV2.loadCartItems());
        })
      ),
    { dispatch: false }
  );

  updateUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.updateUser),
        switchMap(({ user: userData, trackerId: contextId }) => {
          this.loaderService.loaderAction('update-user-profile', 'loading');
          return this.authService.updateUser(userData, { contextId }).pipe(
            map((res) => {
              this.loaderService.loaderAction('update-user-profile', 'success');
              return res && this.store.dispatch(AuthActions.updateUserSuccess({ user: res }));
            }),
            catchError(() => {
              this.loaderService.loaderAction('update-user-profile', 'failed');
              return of(undefined);
            })
          );
        })
      ),
    { dispatch: false }
  );

  updateUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.updateUserSuccess),
        tap((data) => {
          this.router.navigate(['/student/account/profile']);
        })
      ),
    { dispatch: false }
  );

  signup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.signup),
      switchMap(({ email, password, name, geo, contextId }) =>
        this.authService.signup({ email, password, name, geo }, { contextId }).pipe(
          map(() => AuthActions.signupSuccess({ email, password, name })),
          catchError((error) => of(AuthActions.signupError({ error: error.message })))
        )
      )
    )
  );

  signupSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.signupSuccess),
        tap((data) => {
          // // Login After successful signup
          // this.cartService.mergedGuestCart();
          this.store.dispatch(
            AuthActions.login({ data: { email: data.email, password: data.password, remember: true } })
          );
          // Data layer
          // event | signUpMethod, | userRole | userName? | userEmail?
          this.utilsService.dataLayerActions(DataLayerEvent.SIGN_UP, {
            signUpMethod: 'email',
            userRole: 'student',
            userName: data.name || '',
            userEmail: data.email,
          });
        })
      ),
    { dispatch: false }
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        switchMap(() => this.authService.logout())
        // tap(() => this.router.navigate([ROUTE_HOME]))
      ),
    { dispatch: false }
  );

  signupInstructorSuccess$ = createEffect(() => this.actions$.pipe(ofType(AuthActions.signupInstructorSuccess)), {
    dispatch: false,
  });

  afterSuccess() {
    this.store.dispatch(CartActionsV2.initUserCart());
    // this.store.dispatch(AuthActions.userAuthInfoLoad());
    this.authService.loginActionState.pipe(take(1)).subscribe((loginMetadata) => {
      if (loginMetadata.openLoginModal) {
        this.authService.loginActionState.next({ openLoginModal: false });
      }
      setTimeout(() => {
        // this.cartService.mergedGuestCart();
      }, 3000);
      if (
        window.location.pathname === '/account/signup' ||
        window.location.pathname === '/en/account/signup' ||
        window.location.pathname === '/auth' ||
        window.location.pathname === '/en/auth'
      ) {
        // TODO: Enabled the redirect router below when release for public and remove loadTempMethod()
        // this.router.navigate([loginMetadata.redirect || ROUTE_HOME]);
        this.loadTempMethod();
      }
    });
  }

  loadTempMethod() {
    this.authService.getAuthInfo().subscribe({
      next: (authInfo) => {
        if (authInfo.is_instructor) {
          this.router.navigate(['/instructor/courses']);
        } else {
          this.router.navigate(['/instructor/register']);
        }
      },
    });
  }

  // Auth info
  userAuthInfoLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.userAuthInfoLoad),
      tap(() => {
        setTimeout(() => {
          this.loaderService.loaderAction('loading-me', 'loading');
        });
      }),
      switchMap(() => {
        return this.authService.getAuthInfo().pipe(
          map((authInfo) => {
            return AuthActions.userAuthInfoLoaded({ authInfo });
          }),
          catchError((error) => {
            setTimeout(() => {
              this.loaderService.loaderAction('loading-me', 'failed');
            }, 500);
            return of(AuthActions.signupError({ error: error.message }));
          })
        );
      })
    )
  );

  userAuthInfoLoaded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.userAuthInfoLoaded),
        tap((data) => {
          this.loaderService.loaderAction('loading-me', 'success');
        })
      ),
    { dispatch: false }
  );

  loginWithGoogle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginWithGoogle),
      switchMap(({ idToken }) => {
        this.loaderService.loaderAction('loading-me', 'loading');
        return this.authService.singInWIthGoogle(idToken).pipe(
          map((res) => {
            // Data layer
            if (res.signup) {
              this.utilsService.dataLayerActions(DataLayerEvent.SIGN_UP, {
                signUpMethod: 'google',
                userRole: 'student',
              });
            }
            return AuthActions.loginSuccess();
          }),
          catchError((error) => {
            this.loaderService.loaderAction('loading-me', 'failed');
            return of(AuthActions.loginError({ error: error.message }));
          })
        );
      })
    )
  );

  loginAsAdmin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginAsAdmin),
      switchMap(({ credentials }) => {
        this.loaderService.loaderAction('loading-me', 'loading');
        return this.authService.loginAsAdmin(credentials).pipe(
          map((res) => {
            // Data layer
            this.utilsService.dataLayerActions(DataLayerEvent.LOGIN_AS_USER, {
              userRole: 'admin',
            });
            return AuthActions.loginSuccess();
          }),
          catchError((error) => {
            this.loaderService.loaderAction('loading-me', 'failed');
            return of(AuthActions.loginError({ error: error.message }));
          })
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private authService: AuthenticationService,
    private router: Router,
    private store: Store,
    private cartService: CartServiceV2,
    private loaderService: LoaderService,
    private utilsService: UtilsService
  ) {}
}
